import { useState } from "react";
import { Link } from "react-router-dom";
import { API_ENDPOINTS } from "../../config/api";
import { featureFlags } from "../../config/featureFlags";
import { useAuth } from "../../components/AuthProvider";
import { useI18n } from "../../i18n";
import { GovernanceListingSection } from "../../components/governance/GovernanceListingSection";
import { ComplianceCenterListRow } from "../../components/compliance-center/ComplianceCenterListRow";
import { Button } from "../../components/ui/Button";
import listStyles from "@/styles/pages/governance/GovernanceListing.module.scss";

type ActionItem = { key: string; label: string; method?: "GET" | "POST"; endpoint: string; payload?: Record<string, unknown> };

const actions: ActionItem[] = [
  { key: "health", label: "Load AI health", method: "GET", endpoint: API_ENDPOINTS.AI_HEALTH },
  { key: "gap", label: "Run gap analysis", method: "POST", endpoint: API_ENDPOINTS.AI_GAP_ANALYSIS, payload: { standardCode: "ALL" } },
  { key: "remediation", label: "Run remediation plan", method: "POST", endpoint: API_ENDPOINTS.AI_REMEDIATION_PLAN, payload: { standardCode: "ALL" } },
  { key: "evidence", label: "Run evidence review", method: "POST", endpoint: API_ENDPOINTS.AI_EVIDENCE_REVIEW, payload: { standardCode: "ALL" } },
  { key: "qa", label: "Ask auditor Q&A", method: "POST", endpoint: API_ENDPOINTS.AI_AUDITOR_QA, payload: { question: "What are top blockers?", standardCode: "ALL" } },
  { key: "policy", label: "Generate policy draft", method: "POST", endpoint: API_ENDPOINTS.AI_POLICY_GENERATION, payload: { topic: "Access Control", standardCode: "ISO27001" } },
  { key: "process", label: "Generate process draft", method: "POST", endpoint: API_ENDPOINTS.AI_PROCESS_GENERATION, payload: { processType: "Incident Response" } },
  { key: "graph", label: "Load knowledge graph", method: "GET", endpoint: API_ENDPOINTS.AI_KNOWLEDGE_GRAPH },
  { key: "metrics", label: "Load AI metrics", method: "GET", endpoint: API_ENDPOINTS.AI_METRICS },
];

export default function ComplianceCenterAiAssistantWorkspace() {
  const { t } = useI18n();
  const { token } = useAuth();
  const [runningKey, setRunningKey] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [effectiveConfig, setEffectiveConfig] = useState<any>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);

  const disabled = !featureFlags.AI_AUDIT_ASSISTANT_V1;
  const configStatus = String(effectiveConfig?.status || effectiveConfig?.reason || "");
  const hasProviders = Array.isArray(effectiveConfig?.providers) && effectiveConfig.providers.length > 0;
  const hasEnabledProviders = Array.isArray(effectiveConfig?.providers)
    ? effectiveConfig.providers.some((provider: any) => Boolean(provider?.enabled))
    : false;

  const loadEffectiveConfig = async () => {
    setConfigLoading(true);
    setConfigError(null);
    try {
      const res = await fetch(API_ENDPOINTS.AI_EFFECTIVE_CONFIG, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json?.error || `Failed to load effective config (${res.status})`);
      }
      setEffectiveConfig(json?.data || null);
    } catch (error: any) {
      setConfigError(error?.message || "Failed to load effective config.");
    } finally {
      setConfigLoading(false);
    }
  };

  return (
    <div className={listStyles.pageStack}>
      <GovernanceListingSection
        title={t("complianceCenterUi.nav.aiAssistant", "AI Assistant")}
        description={
          <>
            {t("complianceCenterUi.aiIntro", "AI actions are manual only. Configure providers in")}{" "}
            <Link className="text-blue-700 font-semibold underline" to="/integrations">
              {t("complianceCenterUi.integrations", "Integrations")}
            </Link>
            {t("complianceCenterUi.aiIntroSuffix", ", then use this workspace for controlled runs.")}
          </>
        }
      >
        {disabled ? (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 m-0">
            {t(
              "complianceCenterUi.aiDisabled",
              "AI assistant features are disabled by feature flags. Enable AI flags and provider configuration in integrations first.",
            )}
          </p>
        ) : null}
      </GovernanceListingSection>

      <GovernanceListingSection
        title={t("complianceCenterUi.providerConfiguration", "Provider configuration")}
        toolbar={
          <Button type="button" size="small" color="outline" disabled={configLoading} onClick={() => void loadEffectiveConfig()}>
            {configLoading ? t("common.loading", "Loading…") : t("complianceCenterUi.refreshConfig", "Refresh config")}
          </Button>
        }
      >
        <p className="text-xs text-slate-500 m-0 mb-3">
          {t(
            "complianceCenterUi.providerConfigHint",
            "Configure AI provider keys and flags in Integrations first, then refresh to verify effective runtime config.",
          )}
        </p>
        {configError ? <p className="text-xs text-red-600 m-0 mb-2">{configError}</p> : null}
        {!effectiveConfig ? (
          <p className="text-sm text-slate-500 m-0 italic">{t("complianceCenterUi.noConfigLoaded", "No effective config loaded.")}</p>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-slate-600 m-0">
              {t("complianceCenterUi.runtimeStatus", "Runtime status")}:{" "}
              {configStatus || (hasEnabledProviders ? "CONFIGURED" : hasProviders ? "ALL_PROVIDERS_DISABLED" : "NO_PROVIDERS_CONFIGURED")}
            </p>
            <div className="flex flex-col gap-2">
              {(effectiveConfig.providers || []).map((provider: any) => (
                <ComplianceCenterListRow
                  key={provider.providerKey}
                  title={provider.displayName || provider.providerKey}
                  meta={`${t("common.enabled", "Enabled")}: ${provider.enabled ? t("common.yes", "Yes") : t("common.no", "No")} · Key: ${provider.maskedApiKey || "not set"}`}
                  detail={
                    <div className="flex gap-2 mt-2">
                      <Button
                        type="button"
                        size="small"
                        color="outline"
                        disabled={disabled}
                        onClick={async () => {
                          const res = await fetch(API_ENDPOINTS.AI_PROVIDER_CONFIG_TEST(provider.providerKey), {
                            method: "POST",
                            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                            body: JSON.stringify({}),
                          });
                          const json = await res.json().catch(() => ({}));
                          setResult({ action: `Provider test: ${provider.providerKey}`, ok: res.ok, data: json?.data || json });
                        }}
                      >
                        {t("common.test", "Test")}
                      </Button>
                      <Button
                        type="button"
                        size="small"
                        color="outline"
                        disabled={disabled}
                        onClick={async () => {
                          const res = await fetch(API_ENDPOINTS.AI_PROVIDER_CONFIG_DISABLE(provider.providerKey), {
                            method: "POST",
                            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                            body: JSON.stringify({}),
                          });
                          const json = await res.json().catch(() => ({}));
                          setResult({ action: `Disable provider: ${provider.providerKey}`, ok: res.ok, data: json?.data || json });
                        }}
                      >
                        {t("common.disable", "Disable")}
                      </Button>
                    </div>
                  }
                />
              ))}
            </div>
          </div>
        )}
      </GovernanceListingSection>

      <GovernanceListingSection title={t("complianceCenterUi.aiActions", "AI actions")}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {actions.map((action) => (
            <Button
              key={action.key}
              type="button"
              color="outline"
              size="small"
              className="justify-start text-left h-auto py-2"
              disabled={disabled || runningKey === action.key}
              onClick={async () => {
                setRunningKey(action.key);
                try {
                  const res = await fetch(action.endpoint, {
                    method: action.method || "GET",
                    headers: {
                      Authorization: `Bearer ${token}`,
                      "Content-Type": "application/json",
                    },
                    body: action.method === "POST" ? JSON.stringify(action.payload || {}) : undefined,
                  });
                  const json = await res.json().catch(() => ({}));
                  setResult({
                    action: action.label,
                    ok: res.ok,
                    data: json?.data || json,
                    disclaimer: json?.data?.disclaimer,
                    confidence: json?.data?.confidence,
                    sourceReferences: json?.data?.sourceReferences,
                  });
                } finally {
                  setRunningKey(null);
                }
              }}
            >
              {action.label}
            </Button>
          ))}
        </div>
      </GovernanceListingSection>

      <GovernanceListingSection title={t("complianceCenterUi.aiResponse", "Response")}>
        {!result ? (
          <p className="text-sm text-slate-500 m-0 italic">{t("complianceCenterUi.runActionHint", "Run an action to view AI response metadata.")}</p>
        ) : (
          <ul className="text-sm text-slate-700 m-0 p-0 list-none space-y-1">
            <li>
              {t("complianceCenterUi.action", "Action")}: {result.action}
            </li>
            <li>
              {t("common.status", "Status")}: {result.ok ? t("common.success", "success") : t("common.failed", "failed")}
            </li>
            <li>
              {t("complianceCenterUi.confidence", "Confidence")}: {result.confidence ?? "n/a"}
            </li>
            <li>
              {t("complianceCenterUi.sourceReferences", "Source references")}:{" "}
              {Array.isArray(result.sourceReferences) ? result.sourceReferences.length : 0}
            </li>
            <li>{result.disclaimer || t("complianceCenterUi.aiDisclaimer", "AI outputs require human verification.")}</li>
          </ul>
        )}
      </GovernanceListingSection>
    </div>
  );
}
